import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ErrorOutlined } from '@mui/icons-material';

import Button from 'components/library/Button/Button';
import { redirectToSignature } from 'services/network/api/payment';

import styles from './ConfirmationRedirection.module.css';
import { AxiosError } from 'axios';
import { logError } from 'utils/logs';

export default function ConfirmationRedirection({ back }: { back: () => void }) {
  const [hasTechnicalError, setHasTechnicalError] = useState(false);
  const [error, setError] = useState(undefined);
  const [redirectLink, setRedirectLink] = useState('');

  const { t } = useTranslation();
  const { id: projectId } = useParams();

  const signDocument = async () => {
    try {
      setHasTechnicalError(false);
      const { data } = await redirectToSignature(parseInt(projectId || '0'));
      setRedirectLink(data.url);
      window.setTimeout(() => document.location.assign(data.url), 1000);
    } catch (err) {
      logError(err as Error);
      setHasTechnicalError(true);
      if (err instanceof AxiosError) {
        setError(err.response?.data?.error || err);
      }
    }
  }

  useEffect(() => {
    signDocument();
  }, [projectId]);

  if (hasTechnicalError) {
    return (
      <div className={`fadeIn ${styles.background}`}>
        <section className={`bounceIn ${styles.modal}`}>
          <div className={styles.illustration}>
            <ErrorOutlined className={styles.icon} />
          </div>
          <div className={styles.error} data-cy="confirmation-technical-error">
            <p className={`${styles.title}`}>
              {t('signature.technicalError.title')}
            </p>
            <p className={styles.description}>
              {error || t('signature.technicalError.description')}
            </p>
            <Button
              label={t('coordonnate.loi.try')}
              className={styles.button}
              onButtonClick={signDocument}
            />
            <Button
              label={t('coordonnate.loi.back')}
              variant="ghost"
              onButtonClick={back}
            />
          </div>
        </section>
      </div>
    );
  }
  return (
    <div className={styles.container}>
      <section className={styles.content}>
        <h1 className={styles.title}>
          {t('signature.title')}
        </h1>
        {
          (redirectLink)
          && (
            <p>
              {`${t('signature.redirectionHint')} `}
              <a
                data-cy="confirmation-redirection-link"
                href={redirectLink}
              >
                {redirectLink}
              </a>
            </p>
          )
        }
        <div className={styles.waiting} data-cy="redirection-ongoing">
          <FontAwesomeIcon className={styles.loading} icon={faSpinner} spin />
          <p>
            {t('signature.redirection')}
          </p>
        </div>
      </section>
    </div>
  );
}
